import api, { getApiTenantContext } from './api'
import type { AccessContext } from '../types/access'

export type AdminRealtimeEvent = {
  id: string
  type: string
  tenantId: string
  orgId?: string | null
  orgUnitId?: string | null
  locationId?: string | null
  workerProfileId?: string | null
  jobId?: string | null
  shiftId?: string | null
  assignmentId?: string | null
  relationshipId?: string | null
  threadId?: string | null
  userId?: string | null
  action: string
  affectedResources: string[]
  occurredAt: string
  version?: number
}

export type AdminPanelKey =
  | 'overview'
  | 'organizations'
  | 'relationships'
  | 'org-tree'
  | 'users-access'
  | 'workers'
  | 'sharing'
  | 'skills'
  | 'schedule'
  | 'jobs'
  | 'worklog'
  | 'timesheets'
  | 'payroll'
  | 'messages'
  | 'reports'
  | 'settings'
  | 'locations'

const eventPanelMap: Record<string, AdminPanelKey[]> = {
  'jobs.changed': ['jobs', 'schedule', 'overview', 'reports'],
  'shifts.changed': ['schedule', 'jobs', 'timesheets', 'overview', 'reports'],
  'assignments.changed': ['schedule', 'jobs', 'workers', 'timesheets', 'overview'],
  'applications.changed': ['schedule', 'jobs'],
  'locations.changed': ['locations', 'schedule', 'overview'],
  'geofences.changed': ['locations', 'schedule'],
  'workers.changed': ['workers', 'schedule', 'overview', 'timesheets'],
  'worker_skills.changed': ['workers', 'jobs', 'schedule'],
  'worker_documents.changed': ['workers'],
  'punch_status.changed': ['overview', 'locations', 'timesheets'],
  'live_tracking.changed': ['locations'],
  'timesheets.changed': ['timesheets', 'payroll', 'reports', 'overview'],
  'approvals.changed': ['timesheets', 'payroll', 'reports', 'overview'],
  'payroll.changed': ['payroll', 'reports'],
  'messages.changed': ['messages'],
  'notifications.changed': ['messages'],
  'branding.changed': ['settings'],
  'relationships.changed': ['relationships', 'locations', 'jobs'],
  'relationship_grants.changed': ['relationships', 'locations', 'jobs'],
  'users.changed': ['users-access'],
  'roles.changed': ['users-access'],
  'access_context.changed': ['users-access'],
  'organizations.changed': ['organizations', 'relationships', 'org-tree'],
  'org_units.changed': ['org-tree', 'locations'],
  'settings.changed': ['settings'],
  'reports.changed': ['reports', 'overview'],
}

const debounceTimers = new Map<string, number>()

export const panelsForAdminEvent = (event: AdminRealtimeEvent): AdminPanelKey[] => {
  const panels = new Set<AdminPanelKey>(eventPanelMap[event.type] || [])
  for (const resource of event.affectedResources || []) {
    if (resource === 'dashboard') panels.add('overview')
    if (resource === 'access_context') panels.add('users-access')
    if (resource === 'branding') panels.add('settings')
  }
  return Array.from(panels)
}

export function schedulePanelInvalidation(
  event: AdminRealtimeEvent,
  onInvalidate: (panels: AdminPanelKey[], event: AdminRealtimeEvent) => void,
  delayMs = 350,
) {
  const panels = panelsForAdminEvent(event)
  if (!panels.length) return
  const key = `${event.type}:${panels.sort().join(',')}`
  window.clearTimeout(debounceTimers.get(key))
  debounceTimers.set(key, window.setTimeout(() => {
    debounceTimers.delete(key)
    onInvalidate(panels, event)
  }, delayMs))
}

// Bounded exponential backoff. Reset to the front of this sequence once a
// connection has stayed open long enough to be considered stable.
const RECONNECT_BACKOFF_MS = [1_000, 2_000, 5_000, 10_000, 30_000]
const STABLE_CONNECTION_MS = 45_000

// Module-level generation counter. Guards against React Strict Mode (or any
// effect rerun) leaving two live EventSource instances: each call to
// openAdminRealtimeStream bumps the generation, and any callback tied to a
// stale generation becomes a no-op instead of touching shared state.
let streamGeneration = 0

const sanitizeForLog = (url: string) => url.replace(/([?&]ticket=)[^&]+/i, '$1[redacted]')

async function mintAdminEventsTicket(params: {
  tenantId: string
  sessionId: string
}): Promise<{ ticket: string | null; authDenied: boolean }> {
  try {
    const response = await api.post('/api/admin/events/ticket', {
      tenantId: params.tenantId,
      sessionId: params.sessionId,
    })
    const ticket=response.data?.ticket
    return {ticket:typeof ticket==='string'&&ticket.trim()?ticket:null,authDenied:false}
  } catch (error:any) {
    return {ticket:null,authDenied:error?.response?.status===401||error?.response?.status===403}
  }
}

export function openAdminRealtimeStream(
  accessContext: AccessContext,
  options: {
    selectedOrgId?: string | null
    selectedLocationId?: string | null
    activeWorkspace?: string | null
    onEvent: (event: AdminRealtimeEvent) => void
    onAuthFailure?: () => void
  },
) {
  const tenantId = getApiTenantContext() || accessContext.tenantId || accessContext.tenant?.id
  if (!tenantId) return () => undefined

  const generation = ++streamGeneration
  const isCurrent = () => generation === streamGeneration

  // Correlation ID persistence is optional; server ticket authorization is unchanged.
  let sessionId = ''
  try { sessionId = window.sessionStorage.getItem('punch.adminRealtimeSessionId') || '' } catch { /* Use this stream's ID. */ }
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).slice(2)}`
    try { window.sessionStorage.setItem('punch.adminRealtimeSessionId', sessionId) } catch { /* Keep it for this stream only. */ }
  }

  let reconnectTimer: number | null = null
  let stableTimer: number | null = null
  let closed = false
  let source: EventSource | null = null
  let backoffIndex = 0
  let consecutiveAuthFailures = 0

  const clearTimers = () => {
    if (reconnectTimer) window.clearTimeout(reconnectTimer)
    if (stableTimer) window.clearTimeout(stableTimer)
    reconnectTimer = null
    stableTimer = null
  }

  const scheduleReconnect = () => {
    if (closed || !isCurrent()) return
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      // Wait for connectivity instead of burning through backoff while offline.
      window.addEventListener('online', () => connect(), { once: true })
      return
    }
    const delay = RECONNECT_BACKOFF_MS[Math.min(backoffIndex, RECONNECT_BACKOFF_MS.length - 1)]
    backoffIndex = Math.min(backoffIndex + 1, RECONNECT_BACKOFF_MS.length - 1)
    reconnectTimer = window.setTimeout(connect, delay)
  }

  const connect = async () => {
    if (closed || !isCurrent()) return
    const result = await mintAdminEventsTicket({ tenantId, sessionId })
    const ticket=result.ticket
    if (closed || !isCurrent()) return

    if (!ticket) {
      // Transport failures, 5xx responses and malformed successes do not
      // establish that authentication changed. Keep the current workspace.
      consecutiveAuthFailures = result.authDenied ? consecutiveAuthFailures + 1 : 0
      if (consecutiveAuthFailures >= 3) {
        options.onAuthFailure?.()
        return
      }
      scheduleReconnect()
      return
    }

    consecutiveAuthFailures = 0
    const params = new URLSearchParams({ ticket })
    if (options.selectedOrgId) params.set('selectedOrgId', options.selectedOrgId)
    if (options.selectedLocationId) params.set('selectedLocationId', options.selectedLocationId)
    if (options.activeWorkspace) params.set('activeWorkspace', options.activeWorkspace)

    const url = `/api/admin/events/stream?${params.toString()}`
    source?.close()
    source = new EventSource(url, { withCredentials: true })

    source.onopen = () => {
      consecutiveAuthFailures = 0
      stableTimer = window.setTimeout(() => {
        backoffIndex = 0
      }, STABLE_CONNECTION_MS)
    }
    source.onerror = () => {
      if (stableTimer) window.clearTimeout(stableTimer)
      source?.close()
      if (import.meta.env.DEV) console.debug('[adminRealtime] stream error, reconnecting', sanitizeForLog(url))
      if (closed || !isCurrent()) return
      scheduleReconnect()
    }
    Object.keys(eventPanelMap).forEach((eventName) => {
      source?.addEventListener(eventName, (message) => {
        if (closed || !isCurrent()) return
        try {
          options.onEvent(JSON.parse((message as MessageEvent).data))
        } catch {
          // Invalid event payloads are ignored; API refetch remains authoritative.
        }
      })
    })
  }

  connect()

  return () => {
    closed = true
    clearTimers()
    source?.close()
    source = null
  }
}
